// --------------------------------------------------------
// REGISTER CARE LINE - RegisterCareLine
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ContainerWithSidebar from "../../components/UI/containerWithSidebar/ContainerWithSidebar";
import ScreenHeader from "../../components/UI/screenHeader/ScreenHeader";
import Category from "../../models/core/careLine/Category";
import Exam from "../../models/core/careLine/Exam";
import Procedure from "../../models/core/careLine/Procedure";
import VitalSign from "../../models/core/careLine/VitalSign";
import * as careLineActions from "../../store/actions/careLine";
import * as yup from "yup";
import { useAppDispatch } from "../..";
import { Form, Formik } from "formik";
import FormikTextField from "../../components/form/FormikTextField";
import useResponsiveInfo from "../../hooks/useResponsiveInfo";
import {
  GeneralContainer,
  H2,
  Text,
  TextLink,
} from "../../components/UI/generalLayout/Layout.styled";
import BackButton from "../../components/UI/buttons/BackButton";
import Colors from "../../constants/Colors";
import CareLineDropdown from "../../components/dropdown/CareLineDropdown";
import {
  fillCareLineObjectForEdit,
  getGenderObject,
  handleBackendErrors,
  isEmptyObject,
} from "../../helpers/HelperFunctions";
import CareLine from "../../models/core/careLine/CareLine";
import {
  genders,
  initialObjectCareLineField,
  initialObjectCareLineForm,
} from "../../constants/Objects";
import { Paths } from "../../enums/Paths";
import { useLocation, useNavigate } from "react-router-dom";
import { FormContainer } from "../../components/form/Form.styled";
import SuccessModal from "../../components/successModal/SuccessModal";
import { ReactComponent as CareLineModal } from "../../assets/imgs/careLineModal.svg";
import GenericModal from "../../components/genericModal/GenericModal";
import {
  buildFieldList,
  fillCareLineObject,
  getCategoriesOrProceduresForEdit,
  getExamsForEdit,
  getVitalSignsForEdit,
  isFieldlistEmpty,
  removeEmptyObjectsFromList,
  validateEmptyListItem,
  validateListHasAnyEmptyValue,
} from "../../helpers/CareLineHelperFunctions";
import { FieldContainer, RowContainer } from "./CareLine.styled";
import CareLineOccupHealthFieldArea from "../../components/careLineOccupHealthField/CareLineOccupHealthFieldArea";
import CareLineOccupHealthBottomButtonArea from "../../components/careLineOccupHealthField/CareLinOccupHealthBottomButtonArea";
import { CareLineFields } from "../../enums/CareLineFields";

// --------------------------------------------------------
// REGISTER CARE LINE INTERFACE
// --------------------------------------------------------
/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 */
function RegisterCareLine(): JSX.Element {
  // --------------------------------------------------------
  // REGISTER CARE LINE PRIVATE DECLARATIONS
  // --------------------------------------------------------
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state }: any = useLocation();

  const getInitialValues = useCallback(() => {
    if (state !== null) {
      const { careLine }: any = state;
      return fillCareLineObjectForEdit(careLine);
    } else {
      return initialObjectCareLineForm;
    }
  }, [state]);

  const { isMobile } = useResponsiveInfo();
  const [isView] = useState(state !== null && state.isView);
  const [isEdit] = useState(state !== null);
  const [noFieldEditedOnEditState, setNoFieldEditedOnEditState] =
    useState(isEdit);
  const [showModalSucesso, setShowModalSucesso] = useState(false);
  const [showModalVoltar, setShowModalVoltar] = useState(false);
  const [initialValues] = useState(getInitialValues());
  const [minAge, setMinAge] = useState<any>(
    initialValues.idadeInicial !== ""
      ? Number(initialValues.idadeInicial)
      : null
  );
  const [maxAge, setMaxAge] = useState<any>(
    initialValues.idadeFinal !== "" ? Number(initialValues.idadeFinal) : null
  );
  const [generalAgeError, setGeneralAgeError] = useState("");
  const [nomeError, setNomeError] = useState("");
  const [gender, setGender] = useState<any>(
    isView || isEdit
      ? getGenderObject(initialValues.genero)
      : { nome: "Ambos", id: null }
  );
  const [genderError, setGenderError] = useState("");
  const [categories, setCategories] = useState<any[]>(
    isView ? [] : [initialObjectCareLineField]
  );
  const [exams, setExams] = useState<any[]>(
    isView ? [] : [initialObjectCareLineField]
  );
  const [procedures, setProcedures] = useState<any[]>(
    isView ? [] : [initialObjectCareLineField]
  );
  const [vitalSigns, setVitalSigns] = useState<any[]>(
    isView ? [] : [initialObjectCareLineField]
  );
  const [isConfirmationState, setConfirmationState] = useState(false);
  const [categoriaError, setCategoriaError] = useState("");
  const [sinaisVitaisError, setSinaisVitaisError] = useState("");
  const [examesError, setExamesError] = useState("");
  const [procedimentosError, setProcedimentosError] = useState("");
  const [generalErrors, setGeneralErrors] = useState<any[]>([]);

  const allExams: Exam[] = useSelector((state: any) => state.careLine.allExams);
  const allProcedures: Procedure[] = useSelector(
    (state: any) => state.careLine.allProcedures
  );
  const allCategories: Category[] = useSelector(
    (state: any) => state.careLine.allCategories
  );
  const allVitalSigns: VitalSign[] = useSelector(
    (state: any) => state.careLine.allVitalSigns
  );

  const resetErrors = () => {
    setNomeError("");
    setGenderError("");
    setGeneralAgeError("");
    setCategoriaError("");
    setSinaisVitaisError("");
    setExamesError("");
    setProcedimentosError("");
    setGeneralErrors([]);
  };

  useEffect(() => {
    if (maxAge !== null && maxAge !== "" && minAge !== "" && minAge !== null) {
      if (maxAge < minAge) {
        setGeneralAgeError(
          "Idade inicial maior que idade final, não permitido"
        );
      } else {
        setGeneralAgeError("");
      }
    } else {
      setGeneralAgeError("");
    }
  }, [maxAge, minAge]);

  const validationSchema = yup.object().shape({
    nome: yup
      .string()
      .min(3, "Digite pelo menos três caracteres")
      .required("Nome é um campo obrigatório!"),
    idadeInicial: yup.string().nullable(),
    idadeFinal: yup.string().nullable(),
    genero: yup.string().nullable(),
  });

  const register = async (careLine: CareLine) => {
    try {
      await dispatch(careLineActions.registerCareLine(careLine));
      setShowModalSucesso(true);
    } catch (error: any) {
      setConfirmationState(false);
      handleBackendErrors(error, setGeneralErrors);
    }
  };

  const updateCareLine = async (careLine: CareLine, careLineId: string) => {
    try {
      await dispatch(careLineActions.updateCareLine(careLine, careLineId));
      setShowModalSucesso(true);
    } catch (error: any) {
      setConfirmationState(false);
      handleBackendErrors(error, setGeneralErrors);
    }
  };

  const validateCareLineOccupHealthListItems = () => {
    return (
      !isFieldlistEmpty(categories) &&
      (!isFieldlistEmpty(exams) ||
        !isFieldlistEmpty(procedures) ||
        !isFieldlistEmpty(vitalSigns))
    );
  };

  const onClickRegisterCareLine = async (values: any) => {
    try {
      await validationSchema.validate(values, { abortEarly: false });
      if (!validateCareLineOccupHealthListItems()) {
        setGeneralErrors([
          {
            message:
              "Linha de cuidado deverá conter uma Categoria e pelo menos uma das informações: Sinal Vital, Exame ou Procedimento",
          },
        ]);
        return;
      }

      const exames: Exam[] = buildFieldList(exams, CareLineFields.Exame);
      const categorias: Category[] = buildFieldList(
        categories,
        CareLineFields.Categoria
      );
      const procedimentos: Procedure[] = buildFieldList(
        procedures,
        CareLineFields.Procedimento
      );
      const sinaisVitais: VitalSign[] = buildFieldList(
        vitalSigns,
        CareLineFields.SinalVital
      );

      let categoriaError = false;
      let examesError = false;
      let procedimentosError = false;
      let sinaisVitaisError = false;

      if (
        !validateEmptyListItem(categorias) &&
        validateListHasAnyEmptyValue(categories, true, false)
      ) {
        categoriaError = true;
        setCategoriaError("Preencha todas as informações das Categorias");
      } else {
        categoriaError = false;
        setCategoriaError("");
      }

      if (
        !validateEmptyListItem(exames) &&
        validateListHasAnyEmptyValue(exams, true, true)
      ) {
        examesError = true;
        setExamesError("Preencha todas as informações dos Exames");
      } else {
        examesError = false;
        setExamesError("");
      }

      if (
        !validateEmptyListItem(procedimentos) &&
        validateListHasAnyEmptyValue(procedures, true, false)
      ) {
        procedimentosError = true;
        setProcedimentosError(
          "Preencha todas as informações dos Procedimentos"
        );
      } else {
        procedimentosError = false;
        setProcedimentosError("");
      }

      if (
        !validateEmptyListItem(sinaisVitais) &&
        validateListHasAnyEmptyValue(vitalSigns, false, false)
      ) {
        sinaisVitaisError = true;
        setSinaisVitaisError("Preencha todas as informações dos Sinais Vitais");
      } else {
        sinaisVitaisError = false;
        setSinaisVitaisError("");
      }

      if (
        procedimentosError ||
        examesError ||
        categoriaError ||
        sinaisVitaisError ||
        generalAgeError !== ""
      ) {
        return;
      }
      const linhaCuidado = fillCareLineObject(
        exams,
        categories,
        vitalSigns,
        procedures,
        values,
        gender
      );
      resetErrors();
      isConfirmationState
        ? isEdit
          ? await updateCareLine(linhaCuidado, values.id)
          : await register(linhaCuidado)
        : setConfirmationState(true);
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        error.inner.forEach((error: any) => {
          if (error.path) {
            switch (error.path) {
              case "nome":
                setNomeError(error.message);
                break;
              case "genero":
                setGenderError(error.message);
                break;
            }
          }
        });
      }
    }
  };

  const navigateToCareLineList = () => {
    return navigate(Paths.CareLineList);
  };

  const onVoltar = (values: any) => {
    const examesFinal: any[] = exams.filter((e) => {
      return e.nome !== "";
    });

    const categoriasFinal: any[] = categories.filter((c) => {
      return c.nome !== "";
    });

    const procedimentosFinal: any[] = procedures.filter((p) => {
      return p.nome !== "";
    });

    const sinaisVitaisFinal: any[] = vitalSigns.filter((v) => {
      return v.nome !== "";
    });

    if (
      (isEmptyObject(values) &&
        categoriasFinal.length === 0 &&
        procedimentosFinal.length === 0 &&
        examesFinal.length === 0 &&
        sinaisVitaisFinal.length === 0) ||
      isView ||
      noFieldEditedOnEditState
    ) {
      navigateToCareLineList();
    } else {
      setShowModalVoltar(true);
    }
  };

  useEffect(() => {
    if (state !== null) {
      const { careLine }: any = state;
      careLine.exames.length > 0 &&
        setExams(getExamsForEdit(careLine, allExams));
      careLine.categorias.length > 0 &&
        setCategories(
          getCategoriesOrProceduresForEdit(
            true,
            careLine,
            allCategories,
            allProcedures
          )
        );
      careLine.procedimentos.length > 0 &&
        setProcedures(
          getCategoriesOrProceduresForEdit(
            false,
            careLine,
            allCategories,
            allProcedures
          )
        );
      careLine.sinaisVitais.length > 0 &&
        setVitalSigns(getVitalSignsForEdit(careLine, allVitalSigns));
    }
  }, [state, allCategories, allExams, allProcedures, allVitalSigns]);

  const getPageTitle = () => {
    if (isView) {
      return "Visualizar Linha de Cuidado";
    } else if (isEdit) {
      return "Editar Linha de Cuidado";
    } else {
      return "Cadastro de Linha de Cuidado";
    }
  };

  return (
    <ContainerWithSidebar>
      <GeneralContainer>
        <Formik
          initialValues={initialValues}
          onSubmit={() => {}}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {(formik) => {
            const { values, errors, setFieldValue } = formik;

            setMinAge(
              values.idadeInicial !== "" && values.idadeInicial !== null
                ? Number(values.idadeInicial)
                : null
            );
            setMaxAge(
              values.idadeFinal !== "" && values.idadeFinal !== null
                ? Number(values.idadeFinal)
                : null
            );

            if (isEdit) {
              if (
                values.nome !== initialValues.nome ||
                values.idadeFinal !== initialValues.idadeFinal ||
                values.idadeInicial !== initialValues.idadeInicial
              ) {
                setNoFieldEditedOnEditState(false);
              }
            }

            return (
              <Form>
                <BackButton
                  text="Voltar para listagem de linhas de cuidado"
                  onClick={() => {
                    onVoltar(values);
                  }}
                />
                <FormContainer isConfirmationState={isConfirmationState}>
                  <ScreenHeader
                    hasRightButton={false}
                    screenTitle={getPageTitle()}
                    rightButtonText={""}
                    onRightButtonClick={() => {}}
                    errors={generalErrors}
                    onCloseError={() => {
                      setGeneralErrors([]);
                    }}
                  />

                  <FormikTextField
                    variant="standard"
                    name="nome"
                    label="Nome da linha de cuidado*"
                    disabled={isConfirmationState || isView}
                    error={nomeError !== ""}
                    helperText={nomeError !== "" ? nomeError : null}
                    inputProps={{ maxLength: 100 }}
                    sx={{ width: isMobile ? "100%" : "49%", marginBottom: 6 }}
                  />

                  <FieldContainer>
                    <H2>Faixa etária e gênero</H2>
                    <Text style={{ color: Colors.GRAY400 }}>
                      Selecione a faixa etária e o gênero
                    </Text>
                    {generalAgeError !== "" && (
                      <Text color={Colors.error}>{generalAgeError}</Text>
                    )}
                    <RowContainer>
                      <FormikTextField
                        variant="standard"
                        name="idadeInicial"
                        label="Idade inicial"
                        mask="999"
                        disabled={isConfirmationState || isView}
                        error={
                          errors.idadeInicial !== undefined ||
                          generalAgeError !== ""
                        }
                        helperText={
                          errors.idadeInicial !== undefined
                            ? errors.idadeInicial
                            : null
                        }
                        inputProps={{ maxLength: 3, min: 0, max: 999 }}
                        sx={{
                          width: isMobile ? "20%" : "10%",
                          marginRight: 3,
                          marginBottom: 1,
                        }}
                      />
                      <FormikTextField
                        variant="standard"
                        name="idadeFinal"
                        label="Idade final"
                        mask="999"
                        disabled={isConfirmationState || isView}
                        error={
                          errors.idadeFinal !== undefined ||
                          generalAgeError !== ""
                        }
                        helperText={
                          errors.idadeFinal !== undefined
                            ? errors.idadeFinal
                            : null
                        }
                        inputProps={{ maxLength: 3 }}
                        sx={{ width: isMobile ? "20%" : "10%" }}
                      />
                    </RowContainer>
                    <CareLineDropdown
                      value={gender}
                      error={genderError}
                      placeholder="Selecione o gênero"
                      style={{ width: 310 }}
                      onChange={(value: any) => {
                        setNoFieldEditedOnEditState(false);
                        setGender(value);
                        setFieldValue("genero", value.id);
                      }}
                      items={genders}
                      disabled={isConfirmationState || isView}
                    />
                  </FieldContainer>

                  <CareLineOccupHealthFieldArea
                    setNoFieldEditedOnEditState={setNoFieldEditedOnEditState}
                    categories={
                      isConfirmationState
                        ? removeEmptyObjectsFromList(categories)
                        : categories
                    }
                    setCategories={setCategories}
                    categoryError={categoriaError}
                    vitalSigns={
                      isConfirmationState
                        ? removeEmptyObjectsFromList(vitalSigns)
                        : vitalSigns
                    }
                    vitalSignsError={sinaisVitaisError}
                    setVitalSigns={setVitalSigns}
                    exams={
                      isConfirmationState
                        ? removeEmptyObjectsFromList(exams)
                        : exams
                    }
                    examsError={examesError}
                    setExams={setExams}
                    procedures={
                      isConfirmationState
                        ? removeEmptyObjectsFromList(procedures)
                        : procedures
                    }
                    proceduresError={procedimentosError}
                    setProcedures={setProcedures}
                    isConfirmationState={isConfirmationState}
                    isView={isView}
                  />
                </FormContainer>
                <CareLineOccupHealthBottomButtonArea
                  isConfirmationState={isConfirmationState}
                  isFinalizarDisabled={noFieldEditedOnEditState}
                  isView={isView}
                  onEditClick={() => {
                    setConfirmationState(false);
                  }}
                  onCancelClick={() => {
                    onVoltar(values);
                  }}
                  onFinalizarClick={() => {
                    onClickRegisterCareLine(values);
                  }}
                  onVoltarClick={() => {
                    onVoltar(values);
                  }}
                />
              </Form>
            );
          }}
        </Formik>

        <SuccessModal
          Icon={() => {
            return <CareLineModal />;
          }}
          modalMsg="Cadastro da linha de cuidado concluído com sucesso!"
          visible={showModalSucesso}
          BtnText=""
          onVoltar={navigateToCareLineList}
        />

        <GenericModal
          visible={showModalVoltar}
          onCancel={() => {
            setShowModalVoltar(false);
          }}
          onConfirm={navigateToCareLineList}
          modalTitle={
            "Tem certeza que deseja sair do cadastro de linha de cuidado?"
          }
          modalSubtitle={"Os dados preenchidos não serão salvos"}
          cancelButtonMsg={"Confirmar"}
          confirmButtonMsg={"Voltar"}
        />
      </GeneralContainer>
    </ContainerWithSidebar>
  );
}

export default RegisterCareLine;
